import type { PlainDateString, PlainDateTimeString } from '@kp/types';

// https://stackoverflow.com/questions/17415579/how-to-iso-8601-format-a-date-with-timezone-offset-in-javascript
function dateToISO<T = Date>(
  date?: T,
  withTime?: boolean,
  withZone?: boolean,
): T extends string | Date ? string : T extends null ? null : undefined;
/**
 * Converts a Date object or date string to an ISO 8601 date-time string with optional time and time zone.
 *
 * @param date - The Date object or date string to be converted to ISO format.
 * @param withTime - Set to true to include the time in the ISO format.
 * @param withZone - Set to true to include the time zone in the ISO format.
 * @returns An ISO 8601 date-time string or null/undefined based on the input.
 *  — If the input is null or undefined, it returns the same value.
 *  — If the input is a Date object or date string, it returns the corresponding ISO 8601 string.
 *  — If 'withTime' is true, it includes time in the format (e.g., 'yyyy-MM-ddTHH:mm:ss.sssZ').
 *  — If 'withZone' is true, it includes the time zone offset (e.g., 'yyyy-MM-ddTHH:mm:ss.sss+HH:mm').
 */
function dateToISO(
  date?: string | Date | null,
  withTime = false,
  withZone = false,
): string | null | undefined {
  if (!date) {
    return date;
  }

  if (!withTime) {
    return new Date(date).toLocaleString('sv-SE').split(' ')[0];
  }

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    fractionalSecondDigits: 3,
    hour: 'numeric',
    minute: 'numeric',
    month: 'numeric',
    second: 'numeric',
    // oxlint-disable-next-line new-cap
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneName: 'longOffset',
    year: 'numeric',
  };

  const strLocale = new Date(date).toLocaleString('sv-SE', options);

  return strLocale.replace(
    /(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})\s+(?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2}),(?<ms>\d+)\sGMT(?<zone>[+-][\d:]{5})/u,
    withZone
      ? '$<year>-$<month>-$<day>T$<hour>:$<minute>:$<second>$<zone>'
      : '$<year>-$<month>-$<day>T$<hour>:$<minute>:$<second>',
  );
}

/**
 * Converts a Date object or date string to an ISO 8601 date-time string with optional time and time zone.
 *
 * @param date - The date to convert (Date object or date string)
 * @param includeTime - Whether to include time in the output (default: true)
 * @param timeZone - The time zone to use for conversion (default: 'UTC')
 * @returns ISO 8601 formatted date-time string
 *
 * @example
 * ```typescript
 * toISOString(new Date('2023-06-15T10:30:00')); // '2023-06-15T10:30:00.000Z'
 * toISOString('2023-06-15', false); // '2023-06-15'
 * toISOString(new Date(), true, 'Europe/Prague'); // '2023-06-15T12:30:00.000+02:00'
 * ```
 */
// export const toISOString = (
//   date: Date | string,
//   includeTime: boolean = true,
//   timeZone: string = 'UTC',
// ): string => {
//   const dateObj = typeof date === 'string' ? new Date(date) : date;

//   if (isNaN(dateObj.getTime())) {
//     throw new Error('Invalid date provided');
//   }

//   if (!includeTime) {
//     return dateObj.toISOString().split('T')[0];
//   }

//   if (timeZone === 'UTC') {
//     return dateObj.toISOString();
//   }

//   // For non-UTC timezones, use Intl.DateTimeFormat
//   const formatter = new Intl.DateTimeFormat('sv-SE', {
//     timeZone,
//     year: 'numeric',
//     month: '2-digit',
//     day: '2-digit',
//     hour: '2-digit',
//     minute: '2-digit',
//     second: '2-digit',
//     fractionalSecondDigits: 3,
//     timeZoneName: 'longOffset',
//   });

//   const parts = formatter.formatToParts(dateObj);
//   const dateParts: Record<string, string> = {};

//   for (const part of parts) {
//     dateParts[part.type] = part.value;
//   }

//   const {
//     year,
//     month,
//     day,
//     hour,
//     minute,
//     second,
//     fractionalSecond,
//     timeZoneName,
//   } = dateParts;

//   return `${year}-${month}-${day}T${hour}:${minute}:${second}.${fractionalSecond ?? '000'}${timeZoneName}`;
// };

/**
 * Type-safe constructor for a PlainDateString.
 * Formats the value as a YYYY-MM-DD string with the brand attached.
 */
function toPlainDateString(
  date?: string | Date | null,
): PlainDateString | undefined {
  const iso = dateToISO(date, false, false);

  return iso ? (iso as PlainDateString) : undefined;
}

/**
 * Type-safe constructor for a PlainDateTimeString.
 * Formats the value as an ISO timestamp string with the brand attached.
 */
function toPlainDateTimeString(
  date?: string | Date | null,
  withTime = true,
  withZone = true,
): PlainDateTimeString | undefined {
  const iso = dateToISO(date, withTime, withZone);

  return iso ? (iso as PlainDateTimeString) : undefined;
}

export { dateToISO, toPlainDateString, toPlainDateTimeString };
